import { useMemo } from 'react'

import { Audiotrack } from '@mui/icons-material'
import { Tooltip, Typography } from '@mui/material'
import { AudioObject } from '@nativewaves/nw-cloud-sdk/content'
import { ColumnDef } from '@tanstack/react-table'
import { addSeconds, format, formatDistance, fromUnixTime } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { SortableHeader, SortableHeaderRoute, StateVisualizer } from 'domains/Workspaces/Audio/Objects'
import { NoSrcPlaceholderWrapper } from 'domains/Workspaces/Events'
import { formatDuration } from 'utils'

export const useColumns = (route: SortableHeaderRoute) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])

  return useMemo<ColumnDef<AudioObject>[]>(
    () => [
      {
        accessorFn: (row) => ({ src: row.thumbnail?.url, alt: row.name }),
        id: '_thumbnail',
        header: () => null,
        meta: {
          sizing: {
            width: 96,
            px: 2,
          },
          align: 'center',
        },
        cell: () => (
          <NoSrcPlaceholderWrapper>
            <Audiotrack />
          </NoSrcPlaceholderWrapper>
        ),
      },
      {
        accessorFn: (row) => (row.name.length ? row.name : t('noNameSet')),
        id: '_name',
        header: () => <SortableHeader route={route} title={t('name')} sortProp="orderByName" />,
        meta: {
          sizing: {
            minWidth: 300,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) =>
          !!row.estimatedDuration && !!row.estimatedStartTime
            ? formatDistance(row.estimatedStartTime, addSeconds(row.estimatedStartTime, row.estimatedDuration))
            : '',
        header: t('domain:Workspace.Audio.duration'),
        meta: {
          sizing: {
            minWidth: 120,
            maxWidth: 300,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => (row.ended ? 'ended' : row.ended === null ? 'empty' : 'live'),
        id: '_status',
        header: () => t('status'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
          align: 'center',
        },
        cell: (cell) => {
          const state = cell.getValue<'ended' | 'live' | 'empty'>()
          const label = t(state)

          return (
            <StateVisualizer state={state === 'live'} variant="text-sm">
              {label}
            </StateVisualizer>
          )
        },
      },
      {
        accessorKey: 'creationTime',
        header: () => (
          <SortableHeader
            route={route}
            title={t('domain:Workspace.Audio.creationTime')}
            sortProp="orderByCreationTime"
          />
        ),
        meta: {
          sizing: {
            minWidth: 180,
            px: 1,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {format(new Date(getValue<string>()), "yyyy-MM-dd - HH:mm'h'")}
          </Typography>
        ),
      },
      {
        accessorFn: (row) => ({
          deleteAfter: row.activeLifecyclePolicy.deleteAfter,
          creationTimestamp: row.creationTimestamp,
        }),
        header: t('domain:Workspace.Audio.lifecycle'),
        meta: {
          sizing: {
            width: 180,
            px: 2,
          },
        },
        cell: ({ getValue }) => {
          const values = getValue<{ deleteAfter: number | undefined; creationTimestamp: number }>()
          const seconds = values.deleteAfter ? parseInt(values.deleteAfter.toFixed(0)) : null

          const label = seconds
            ? formatDuration(
                seconds,
                { seconds: true, minutes: true, hours: true, days: true, years: true },
                { suffixed: true, longSuffix: true, omitEmptyValues: true },
              )[0]
            : t('domain:Workspace.Audio.contentLifecycleNoExpiry')

          const tooltipTitle = seconds
            ? t('domain:Workspace.Audio.lifecycleDeletion', {
                time: (
                  formatDuration(
                    seconds,
                    { seconds: true, minutes: true, hours: true, days: true, years: true },
                    { suffixed: true, omitEmptyValues: true },
                  ) as string[]
                ).join(' '),
                deletionTime: format(fromUnixTime(values.creationTimestamp + seconds), 'yyyy-MM-dd HH:mm'),
              })
            : t('domain:Workspace.Audio.contentLifecycleNoDeletion')

          return (
            <Tooltip title={tooltipTitle} placement="top">
              <Typography>{label}</Typography>
            </Tooltip>
          )
        },
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [t, route],
  )
}
